import React, { useState, useCallback, useMemo, ChangeEvent, useRef } from 'react';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { observer } from 'mobx-react';
import { BringgInput } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import { head } from 'lodash';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { useOutsideClick } from '@bringg-frontend/hooks';

import DraggableItem from './draggable-item';
import { TranslatableOption } from '../../../../types/common.consts';
import { EXCEPTIONS_COLUMN_IDS } from '../../../../consts';
import { ALWAYS_ACTIVE_FIRST_COLUMNS } from '../../columns/column-utils';
import type { TableConfigObjectAdapter } from '../../../../stores/alerts-view/table-config-object-adapter';

import './styles.scss';

const VISIBILITY_TOGGLE_DISABLED_COLUMN_IDS = [EXCEPTIONS_COLUMN_IDS.ALERT_TYPE, EXCEPTIONS_COLUMN_IDS.ALERT_TIME];

interface Props {
	tableConfig: TableConfigObjectAdapter;
	onOutsideClick: () => void;
}

function ColumnsSelection({ onOutsideClick, tableConfig }: Props) {
	const ref = useRef<HTMLDivElement>();

	const { t } = useTranslation();
	const {
		setTableOrderedColumns,
		toggleHideTableColumn,
		resetTableColumns,
		tableOrderedColumns,
		hiddenTableColumnsSet,
		defaultTableOrderedColumns
	} = tableConfig;

	const [filter, setFilter] = useState('');
	const [columns, setColumns] = useState<TranslatableOption[]>(tableOrderedColumns);

	const onFilterChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setFilter(e.target.value);
	}, []);
	const handleResetColumns = useCallback(() => {
		resetTableColumns();
		setColumns(defaultTableOrderedColumns);
	}, [defaultTableOrderedColumns, resetTableColumns]);

	const filteredColumns = useMemo(() => {
		const key = filter?.trim().toLowerCase();

		if (!key) {
			return columns;
		}

		return columns.filter(column => t(column.translateKey)?.toLowerCase().includes(key));
	}, [columns, filter, t]);

	const moveColumn = useCallback(
		(dragId: string, hoverIndex: number) => {
			const newOrder = [...columns];
			const dragIndexById = columns.findIndex(({ id }) => id === dragId);

			const dragColumn = head(newOrder.splice(dragIndexById, 1));
			newOrder.splice(hoverIndex, 0, dragColumn);

			setColumns(newOrder);
		},
		[columns]
	);

	const handleDroppedItem = useCallback(() => {
		setTableOrderedColumns(columns);
	}, [columns, setTableOrderedColumns]);

	const onColumnStateChanged = useCallback(
		({ target }) => {
			toggleHideTableColumn(target.id);
		},
		[toggleHideTableColumn]
	);

	useOutsideClick(ref, useCallback(onOutsideClick, [onOutsideClick]));

	return (
		<div className="columns-selection" ref={ref}>
			<div className="columns-selection-title">
				<span>{t('EXCEPTIONS_LIST.TABLE_CUSTOMIZATION')}</span>
			</div>
			<div className="columns-selection-saerch">
				<div className="input">
					<BringgInput
						role="search"
						prefix={<BringgIcon iconName={BringgFontIcons.Search} />}
						onChange={onFilterChange}
						value={filter}
					/>
				</div>
				<div className="select-default">
					<span onClick={handleResetColumns}>{t('EXCEPTIONS_LIST.DEFAULT')}</span>
				</div>
			</div>
			<div className="columns-selection-list">
				<DndProvider backend={HTML5Backend}>
					<div className="reorder-columns-container" role="list">
						{filteredColumns.map((column, i) => (
							<DraggableItem
								checked={!hiddenTableColumnsSet.has(column.id)}
								checkboxDisabled={VISIBILITY_TOGGLE_DISABLED_COLUMN_IDS.includes(column.id)}
								index={i}
								key={column.id}
								column={column}
								dragDisabled={Boolean(filter) || ALWAYS_ACTIVE_FIRST_COLUMNS.includes(column.id)}
								moveItem={moveColumn}
								onDropItem={handleDroppedItem}
								onChange={onColumnStateChanged}
							/>
						))}
					</div>
				</DndProvider>
			</div>
		</div>
	);
}

export default observer<typeof ColumnsSelection>(ColumnsSelection);
